/**
 * How a medicine is named and counted on the prescription screens and the printed Rx, in one place.
 * Has no imports, so PrescriptionPrint can share it without a circular import.
 */

// Short dosage-form prefix shown before a medicine name (e.g. "Tab. Calamox", "Syr. Calpol").
const TYPE_ABBREV = {
  TABLET: 'Tab.', CAPSULE: 'Cap.', SYRUP: 'Syr.', SUSPENSION: 'Susp.',
  DROPS: 'Drops', INJECTION: 'Inj.', INHALER: 'Inhaler', CREAM: 'Cream',
  OINTMENT: 'Oint.', GEL: 'Gel', POWDER: 'Sachet', SACHET: 'Sachet',
  SOLUTION: 'Soln.', LOTION: 'Lotion', SPRAY: 'Spray', SUPPOSITORY: 'Supp.',
};

/**
 * The prefix for this type, or '' when the name already starts with it. Prod has "Inj. Lasix 20mg"
 * and "Sachet Core C", which otherwise read "Inj. Inj. Lasix 20mg" and "Sachet Sachet Core C".
 * The abbreviation and the type's own word both count ("Inj." and "Injection").
 */
export const typePrefix = (type, name = '') => {
  const t = String(type || '').toUpperCase();
  const abbrev = TYPE_ABBREV[t];
  if (!abbrev) return '';
  const words = [abbrev.replace(/\.$/, ''), t.toLowerCase()];
  const alreadyNamed = new RegExp(`^(${words.join('|')})\\b`, 'i');
  return alreadyNamed.test(String(name || '').trim()) ? '' : abbrev;
};

const TABLET = ['tablet', 'tablets'];
const CAPSULE = ['capsule', 'capsules'];
const SACHET = ['sachet', 'sachets'];
const BOTTLE = ['bottle', 'bottles'];
const TUBE = ['tube', 'tubes'];

// Packaging that names the container itself. BOX (a "Pack"), STRIP and OTHER name what the units
// came in, so they say nothing about the unit on the shelf.
const CONTAINER_NOUN = {
  BOTTLE, TUBE, SACHET, VIAL: ['vial', 'vials'], AMPOULE: ['ampoule', 'ampoules'],
};

// The word for one unit of stock when the packaging does not name a container.
const TYPE_NOUN = {
  TABLET, CAPSULE, SACHET, POWDER: SACHET,
  SYRUP: BOTTLE, SUSPENSION: BOTTLE, SOLUTION: BOTTLE, DROPS: BOTTLE, LOTION: BOTTLE, SPRAY: BOTTLE,
  CREAM: TUBE, OINTMENT: TUBE, GEL: TUBE,
  INJECTION: ['injection', 'injections'],
  INHALER: ['inhaler', 'inhalers'],
  SUPPOSITORY: ['suppository', 'suppositories'],
  PATCH: ['patch', 'patches'],
};

// Counted one by one whatever they came in: a box of 100 tablets is 100 tablets on the shelf.
const COUNTED_SINGLY = new Set(['TABLET', 'CAPSULE', 'SACHET']);

const unitNounFor = (med) => {
  const type = String(med.medicineType || '').toUpperCase();
  const packaging = String(med.packagingType || '').toUpperCase();
  if (COUNTED_SINGLY.has(type)) return TYPE_NOUN[type];
  return CONTAINER_NOUN[packaging] || TYPE_NOUN[type] || ['unit', 'units'];
};

// The units whose size is worth saying: "bottles of 20 ml" tells a doctor dosing in ml how much
// there is. A tablet's strength is already beside its name.
const SIZED_CONTAINERS = new Set(['bottle', 'tube', 'vial', 'ampoule']);

const fmtNum = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

// What one container holds. A repackaged liquid's dispensing bottle comes first: its stock is
// counted in those small bottles, never the distributor's bottle they were poured from.
const containerSizeOf = (med) => {
  if (Number(med.dispenseSize) > 0) return `${fmtNum(med.dispenseSize)} ${med.sizeUnit || 'ml'}`;
  const size = String(med.containerSize || '').trim();
  return size || null;
};

/**
 * What is on the shelf, worded in the unit stock is counted in, which is the unit handed over:
 * "90 tablets", "12 bottles of 20 ml", "86 injections", "3 tubes". Null when the row carries no
 * count (an external medicine, a template line). Reads the Rx search row's batch total
 * (totalQuantity), else a medicine record's own quantity (the copy on a saved Rx line).
 *
 * `title` adds what does not fit the badge: the total volume for a liquid, and the units already
 * on today's earlier prescriptions that have not been handed over yet (advisory, as in stockState).
 */
export const stockCount = (med) => {
  if (!med) return null;
  const raw = med.totalQuantity ?? med.quantity;
  if (raw == null || raw === '' || !Number.isFinite(Number(raw))) return null;
  const n = Math.max(0, Number(raw));
  const [one, many] = unitNounFor(med);
  const size = SIZED_CONTAINERS.has(one) ? containerSizeOf(med) : null;
  const text = `${fmtNum(n)} ${n === 1 ? one : many}${size ? ` of ${size}` : ''}`;

  const sizeParts = size && size.match(/^(\d+(?:\.\d+)?)\s*(ml|g)\b/i);
  const total = sizeParts && n > 1 ? ` (${fmtNum(n * Number(sizeParts[1]))} ${sizeParts[2].toLowerCase()} in all)` : '';
  const pending = Number(med.pendingQuantity) || 0;
  const promised = pending > 0
    ? ` ${fmtNum(pending)} of them ${pending === 1 ? 'is' : 'are'} on today's earlier prescriptions, not yet handed over.`
    : '';
  return { count: n, text, title: `${text} in stock${total}.${promised}` };
};
